import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { Loader } from 'lucide-react'
import PropTypes from 'prop-types'

import { cn } from '@/utils/helpers'

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'border-[1.5px] border-input bg-background text-destructive shadow-xs hover:bg-destructive/10 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border-[1.5px] border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        plain: '',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        plain: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const loaderVariants = cva('absolute left-3 h-5 w-5 animate-spin', {
  variants: {
    variant: {
      default: 'text-muted',
      destructive: 'text-primary',
      outline: 'text-muted',
      secondary: 'text-primary',
      ghost: 'text-muted',
      link: 'text-muted',
      plain: 'text-muted',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function Button({
  className = '',
  variant = 'default',
  size = 'default',
  asChild = false,
  disabled = false,
  loading = false,
  children,
  ...props
}) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      type="button"
      className={cn(buttonVariants({ variant, size, className }), loading && '!pl-9')}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader className={cn(loaderVariants({ variant }))} />}
      <Slottable>{children}</Slottable>
    </Comp>
  )
}

Button.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  size: PropTypes.string,
  variant: PropTypes.string,
}

export { Button, buttonVariants }
